import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { usd, pct, ago } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/shell/PageHeader";
import {
  EmptyState,
  Panel,
  StatusBadge,
} from "@/components/common/primitives";
import {
  MODE_GUIDANCE,
  buildSearchPayload,
  filterOffers,
  formatEconomics,
  isTerminalStatus,
  offerKey,
  validateFinderForm,
} from "@/lib/productFinder";
import {
  Compass,
  Filter,
  Loader2,
  Save,
  Search,
  Sparkles,
  XCircle,
  ExternalLink,
  Ban,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_FORM = {
  title: "",
  identifier: "",
  identifierType: "",
  walmartUrl: "",
  walmartItemId: "",
  searchMode: "quick",
  zipCode: "",
  radiusMiles: "25",
  minimumRoi: "",
  allowAiReview: false,
  allowProviderEscalation: false,
};

const DEFAULT_FILTERS = {
  verifiedOnly: false,
  inStock: false,
  fulfillment: "",
  positiveRoi: false,
  minimumRoi: "",
  source: "",
  minPrice: "",
  maxPrice: "",
  freshWithinHours: "",
};

function Field({ id, label, children, hint }) {
  return (
    <label htmlFor={id} className="block min-w-0">
      <span className="text-micro font-medium uppercase tracking-wide text-ink-muted">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-micro text-ink-muted">{hint}</span> : null}
    </label>
  );
}

function inputClassName() {
  return "mt-1 w-full h-9 px-3 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact text-ink-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40 focus-visible:border-emerald-600";
}

function EconomicsBlock({ offer, verified }) {
  if (!verified) {
    return (
      <p className="text-compact text-ink-muted" data-testid="review-no-roi">
        Profit and ROI are hidden until the match is verified.
      </p>
    );
  }
  const econ = formatEconomics(offer);
  if (econ.kind === "missing") {
    return (
      <p className="text-compact text-ink-muted" data-testid="missing-roi">
        Economics unavailable
      </p>
    );
  }
  if (econ.kind === "loss") {
    return (
      <div data-testid="negative-roi" className="space-y-0.5">
        <p className="text-compact font-medium text-red-600">
          {econ.label}
          {econ.profit != null ? `: ${usd(econ.profit)}` : ""}
        </p>
        {econ.roi != null ? (
          <p className="text-compact text-red-600" aria-label={`ROI ${econ.roi}%`}>
            ROI: {pct(econ.roi)}
          </p>
        ) : null}
      </div>
    );
  }
  return (
    <div data-testid="positive-economics" className="space-y-0.5">
      <p className="text-compact font-medium text-emerald-700">
        {econ.label}
        {econ.profit != null ? `: ${usd(econ.profit)}` : ""}
      </p>
      {econ.roi != null ? (
        <p className="text-compact text-ink-secondary">ROI: {pct(econ.roi)}</p>
      ) : null}
      {offer.margin != null ? (
        <p className="text-compact text-ink-muted">Margin: {pct(offer.margin)}</p>
      ) : null}
    </div>
  );
}

function OfferCard({
  offer,
  bucket,
  index,
  jobId,
  onSave,
  onDismiss,
  verified,
}) {
  const key = offerKey(offer, bucket, index);
  return (
    <article
      data-testid={`offer-card-${bucket}-${index}`}
      className="rounded-panel border border-edge-subtle bg-surface-0 p-4 space-y-3"
      aria-label={`${verified ? "Verified" : "Review"} offer from ${offer.source_name || offer.source_id || "source"}`}
    >
      {(offer.image_url || offer.image) ? (
        <a
          href={offer.source_url || offer.image_url || offer.image}
          target="_blank"
          rel="noreferrer"
          className="block w-fit"
          aria-label="Open supplier product"
        >
          <img
            src={offer.image_url || offer.image}
            alt={offer.title || offer.source_name || "Supplier product"}
            className="h-24 w-24 rounded-ctrl border border-edge-subtle bg-white object-contain"
            loading="lazy"
          />
        </a>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={verified ? "success" : "warning"} testid={verified ? "badge-verified" : "badge-review"}>
              {verified ? "Verified" : "Needs review"}
            </StatusBadge>
            {offer.identity_tier ? (
              <span className="text-micro ident text-ink-muted">Tier {offer.identity_tier}</span>
            ) : null}
          </div>
          <h3 className="mt-2 text-body font-medium text-ink-primary break-words">
            {offer.source_name || offer.source_id || "Source offer"}
          </h3>
          <p className="text-compact text-ink-secondary break-all">
            {offer.source_url || "No product URL"}
          </p>
        </div>
        {offer.source_url ? (
          <a
            href={offer.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-8 items-center gap-1 rounded-ctrl border border-edge-subtle px-2.5 text-micro hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40"
          >
            {String(offer.source_url || "").includes("4sgm.com")
              ? "View on 4SGM"
              : "Open source"}{" "}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        ) : null}
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2 text-compact">
        <div>
          <dt className="text-micro text-ink-muted">Source price</dt>
          <dd className="font-medium tabnum">{usd(offer.source_price)}</dd>
        </div>
        {verified ? (
          <>
            <div>
              <dt className="text-micro text-ink-muted">Shipping</dt>
              <dd className="tabnum">{usd(offer.shipping)}</dd>
            </div>
            <div>
              <dt className="text-micro text-ink-muted">Est. tax</dt>
              <dd className="tabnum">{usd(offer.tax)}</dd>
            </div>
            <div>
              <dt className="text-micro text-ink-muted">Landed cost</dt>
              <dd className="tabnum font-medium">{usd(offer.landed_cost)}</dd>
            </div>
            <div>
              <dt className="text-micro text-ink-muted">Est. fees</dt>
              <dd className="tabnum">{usd(offer.marketplace_fees)}</dd>
            </div>
          </>
        ) : null}
        <div>
          <dt className="text-micro text-ink-muted">Stock</dt>
          <dd>{offer.availability || "unknown"}</dd>
        </div>
        <div>
          <dt className="text-micro text-ink-muted">Fulfillment</dt>
          <dd>{offer.fulfillment || "—"}</dd>
        </div>
        <div>
          <dt className="text-micro text-ink-muted">Confidence</dt>
          <dd className="tabnum">{offer.confidence != null ? `${offer.confidence}` : "—"}</dd>
        </div>
        <div>
          <dt className="text-micro text-ink-muted">Last checked</dt>
          <dd>{offer.last_checked_at ? ago(offer.last_checked_at) : "—"}</dd>
        </div>
      </dl>

      <EconomicsBlock offer={offer} verified={verified} />

      {!verified && (
        <div className="rounded-ctrl bg-amber-50 border border-amber-100 p-2.5 text-compact text-amber-900">
          <p className="font-medium">Why it needs review</p>
          <p className="mt-0.5">{offer.decision_reason || "Match evidence is incomplete."}</p>
          {(offer.missing_evidence || []).length > 0 ? (
            <p className="mt-1 text-micro">Missing: {(offer.missing_evidence || []).join(", ")}</p>
          ) : null}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {verified ? (
          <button
            type="button"
            data-testid={`save-offer-${index}`}
            onClick={() => onSave(key, "verified")}
            className="h-8 px-3 rounded-ctrl bg-signal-blue text-white text-micro inline-flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-signal-blue"
          >
            <Save className="h-3.5 w-3.5" aria-hidden /> Save opportunity
          </button>
        ) : (
          <>
            <button
              type="button"
              data-testid={`save-review-${index}`}
              onClick={() => onSave(key, "review")}
              className="h-8 px-3 rounded-ctrl border border-edge-subtle text-micro inline-flex items-center gap-1.5 hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40"
            >
              <Save className="h-3.5 w-3.5" aria-hidden /> Save for review
            </button>
            <button
              type="button"
              data-testid={`dismiss-offer-${index}`}
              onClick={() => onDismiss(key, "review")}
              className="h-8 px-3 rounded-ctrl border border-edge-subtle text-micro inline-flex items-center gap-1.5 text-ink-secondary hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40"
            >
              <Ban className="h-3.5 w-3.5" aria-hidden /> Dismiss
            </button>
          </>
        )}
        {jobId ? (
          <a
            href="/app/analyzer"
            className="h-8 px-3 rounded-ctrl border border-edge-subtle text-micro inline-flex items-center hover:bg-surface-2"
          >
            Open in Analyzer
          </a>
        ) : null}
      </div>
    </article>
  );
}

export default function ProductFinder() {
  const formId = useId();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [errors, setErrors] = useState([]);
  const [registry, setRegistry] = useState(null);
  const [job, setJob] = useState(null);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [recent, setRecent] = useState([]);
  const [savedOpps, setSavedOpps] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const pollRef = useRef(null);
  const liveRef = useRef(null);

  const provisional =
    registry &&
    (!registry.authoritative || Number(registry.operational_sources || 0) === 0);

  const loadMeta = async () => {
    try {
      const [st, hist, opps] = await Promise.all([
        api.productSearchesStatus(),
        api.productSearchesRecent({ limit: 10 }),
        api.productSourcingOpportunities({ limit: 8, sort: "newest" }).catch(() => null),
      ]);
      setRegistry(st.data);
      setRecent(hist.data?.items || []);
      setSavedOpps(opps?.data?.items || []);
    } catch (e) {
      setApiError(e?.response?.data?.detail || e?.message || "Failed to load Product Finder");
    }
  };

  useEffect(() => {
    loadMeta();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const refreshJob = async (jobId) => {
    const [p, r, j] = await Promise.all([
      api.productSearchProgress(jobId),
      api.productSearchResults(jobId),
      api.productSearchGet(jobId),
    ]);
    setProgress(p.data);
    setResults(r.data);
    setJob(j.data);
    if (isTerminalStatus(p.data?.status || j.data?.status)) {
      stopPolling();
      loadMeta();
    }
  };

  const startPolling = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(() => {
      refreshJob(jobId).catch(() => stopPolling());
    }, 1500);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validateFinderForm(form);
    setErrors(errs);
    if (errs.length) {
      liveRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setApiError(null);
    setResults(null);
    setProgress(null);
    try {
      const payload = buildSearchPayload(form);
      const res = await api.productSearchesCreate(payload);
      const data = res.data;
      setJob(data);
      setProgress({
        job_id: data.job_id,
        status: data.status,
        sources_selected: data.sources_selected || 0,
        sources_completed: data.sources_completed || 0,
        offers_found: data.offers_found || 0,
        verified_offers: data.verified_offers_count || 0,
        review_offers: data.review_offers_count || 0,
        rejected_offers: data.rejected_offers || 0,
        message: data.message,
        provisional: data.provisional,
      });
      setResults({
        verified_offers: data.verified_offers || [],
        review_offers: data.review_offers || [],
        rejected_count: data.rejected_count || 0,
        ai_explanation: data.ai_explanation,
        message: data.message,
        provisional: data.provisional,
        budget_message: data.budget_message,
      });
      if (!isTerminalStatus(data.status)) {
        startPolling(data.job_id);
      } else {
        loadMeta();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.status === "rate_limited") {
        setApiError(detail.message || "Rate limit reached. Try again shortly.");
        toast.error(detail.message || "Rate limited");
      } else {
        const msg =
          typeof detail === "string"
            ? detail
            : err?.message || "Search failed";
        setApiError(msg);
        toast.error(String(msg));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async () => {
    if (!job?.job_id) return;
    try {
      const res = await api.productSearchCancel(job.job_id);
      setJob(res.data);
      stopPolling();
      toast.message(res.data?.cancel_message || "Search cancelled. Completed results were preserved.");
      await refreshJob(job.job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  const onSave = async (offerRef, bucket) => {
    if (!job?.job_id) return;
    try {
      const res = await api.productSearchSave(job.job_id, { offer_ref: offerRef, bucket });
      toast.success(res.data?.label || "Saved");
      loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const onDismiss = async (offerRef, bucket) => {
    if (!job?.job_id) return;
    try {
      await api.productSearchDismiss(job.job_id, { offer_ref: offerRef, bucket });
      toast.message("Dismissed");
      loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Dismiss failed");
    }
  };

  const reopen = async (jobId) => {
    try {
      await refreshJob(jobId);
      if (!isTerminalStatus(job?.status)) startPolling(jobId);
    } catch (err) {
      toast.error("Could not reopen search");
    }
  };

  const verifiedFiltered = useMemo(
    () => filterOffers(results?.verified_offers || [], { ...filters, verifiedOnly: false }),
    [results, filters],
  );
  const reviewFiltered = useMemo(() => {
    if (filters.verifiedOnly) return [];
    return filterOffers(results?.review_offers || [], filters);
  }, [results, filters]);

  const setMode = (mode) => setForm((f) => ({ ...f, searchMode: mode }));

  return (
    <div className="space-y-4" data-testid="product-finder-page">
      <PageHeader
        title="Product Finder"
        description="Search approved sources for verified and review matches. Economics stay honest — review offers never show actionable ROI."
      />

      <div
        ref={liveRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid="finder-live-region"
      >
        {progress?.status ? `Search status: ${progress.status}` : ""}
        {apiError ? `Error: ${apiError}` : ""}
      </div>

      {provisional && (
        <Panel className="p-5" testid="product-finder-provisional">
          <EmptyState
            icon={Compass}
            title={registry?.provisional_title || "Product Finder is being prepared"}
            hint={
              registry?.provisional_hint ||
              "The sourcing engine is ready, but the approved store registry has not been activated yet. Product searches will become available after the store list is reviewed and enabled."
            }
            testid="product-finder-empty"
          />
          <p className="mt-3 text-center text-micro text-ink-muted">
            Operational sources: {registry?.operational_sources ?? 0}
          </p>
        </Panel>
      )}

      <Panel className="p-4" testid="product-finder-form-panel">
        <form id={formId} onSubmit={onSubmit} noValidate className="space-y-4" data-testid="product-finder-form">
          {errors.length > 0 && (
            <div
              role="alert"
              data-testid="form-error-summary"
              className="rounded-ctrl border border-red-200 bg-red-50 px-3 py-2 text-compact text-red-700"
            >
              <p className="font-medium">Fix the following:</p>
              <ul className="mt-1 list-disc pl-5">
                {errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </div>
          )}
          {apiError && (
            <div
              role="alert"
              data-testid="api-error"
              className="rounded-ctrl border border-red-200 bg-red-50 px-3 py-2 text-compact text-red-700"
            >
              {String(apiError)}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            <Field id={`${formId}-title`} label="Product title">
              <input
                id={`${formId}-title`}
                data-testid="finder-title"
                className={inputClassName()}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="CeraVe Foaming Facial Cleanser 12 oz"
                autoComplete="off"
              />
            </Field>
            <Field id={`${formId}-id`} label="UPC / EAN / GTIN">
              <input
                id={`${formId}-id`}
                data-testid="finder-identifier"
                className={cn(inputClassName(), "ident")}
                value={form.identifier}
                onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                placeholder="012345678901"
                autoComplete="off"
              />
            </Field>
            <Field id={`${formId}-url`} label="Walmart URL">
              <input
                id={`${formId}-url`}
                data-testid="finder-walmart-url"
                className={inputClassName()}
                value={form.walmartUrl}
                onChange={(e) => setForm({ ...form, walmartUrl: e.target.value })}
                placeholder="https://www.walmart.com/ip/..."
                autoComplete="off"
              />
            </Field>
            <Field id={`${formId}-item`} label="Walmart item ID">
              <input
                id={`${formId}-item`}
                data-testid="finder-walmart-item"
                className={cn(inputClassName(), "ident")}
                value={form.walmartItemId}
                onChange={(e) => setForm({ ...form, walmartItemId: e.target.value })}
                placeholder="122628483"
                autoComplete="off"
              />
            </Field>
          </div>

          <fieldset>
            <legend className="text-micro font-medium uppercase tracking-wide text-ink-muted">Search mode</legend>
            <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Search mode">
              {["quick", "deep", "regional"].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={form.searchMode === mode}
                  data-testid={`mode-${mode}`}
                  onClick={() => setMode(mode)}
                  className={cn(
                    "h-9 px-3 rounded-ctrl text-compact capitalize focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40",
                    form.searchMode === mode ? "ez-seg-active" : "ez-seg ez-seg-outline",
                  )}
                >
                  {mode}
                </button>
              ))}
            </div>
            <p className="mt-2 text-compact text-ink-secondary" data-testid="mode-guidance">
              {MODE_GUIDANCE[form.searchMode]}
            </p>
          </fieldset>

          {form.searchMode === "regional" && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="regional-fields">
              <Field id={`${formId}-zip`} label="ZIP code" hint="Required for regional search">
                <input
                  id={`${formId}-zip`}
                  data-testid="finder-zip"
                  className={cn(inputClassName(), "ident")}
                  value={form.zipCode}
                  onChange={(e) => setForm({ ...form, zipCode: e.target.value })}
                  placeholder="30301"
                  inputMode="numeric"
                  required
                />
              </Field>
              <Field id={`${formId}-radius`} label="Radius (miles)">
                <input
                  id={`${formId}-radius`}
                  data-testid="finder-radius"
                  type="number"
                  min="1"
                  max="500"
                  className={inputClassName()}
                  value={form.radiusMiles}
                  onChange={(e) => setForm({ ...form, radiusMiles: e.target.value })}
                />
              </Field>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <Field id={`${formId}-roi`} label="Minimum ROI % (optional)">
              <input
                id={`${formId}-roi`}
                data-testid="finder-min-roi"
                type="number"
                className={inputClassName()}
                value={form.minimumRoi}
                onChange={(e) => setForm({ ...form, minimumRoi: e.target.value })}
                placeholder="30"
              />
            </Field>
            <div className="flex flex-col justify-end gap-2 pt-1">
              <label className="inline-flex items-center gap-2 text-compact text-ink-secondary">
                <input
                  type="checkbox"
                  data-testid="finder-ai-toggle"
                  checked={form.allowAiReview}
                  onChange={(e) => setForm({ ...form, allowAiReview: e.target.checked })}
                  className="rounded border-edge-subtle"
                />
                AI-assisted review
              </label>
              <label className="inline-flex items-center gap-2 text-compact text-ink-secondary">
                <input
                  type="checkbox"
                  data-testid="finder-escalation-toggle"
                  checked={form.allowProviderEscalation}
                  onChange={(e) =>
                    setForm({ ...form, allowProviderEscalation: e.target.checked })
                  }
                  className="rounded border-edge-subtle"
                  disabled={!form.allowAiReview}
                />
                Allow provider escalation
              </label>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="submit"
              data-testid="finder-submit"
              disabled={submitting}
              className="h-9 px-4 rounded-ctrl bg-signal-blue text-white text-compact inline-flex items-center gap-1.5 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-signal-blue"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Search className="h-4 w-4" aria-hidden />}
              Run search
            </button>
          </div>
        </form>
      </Panel>

      {progress && (
        <Panel className="p-4" testid="product-finder-progress">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-stitle text-ink-primary">Search progress</h2>
              <p className="text-compact text-ink-secondary mt-1" data-testid="progress-status">
                Status: <span className="font-medium">{progress.status}</span>
                {progress.elapsed_seconds != null
                  ? ` · ${Math.round(progress.elapsed_seconds)}s`
                  : ""}
              </p>
            </div>
            {!isTerminalStatus(progress.status) && (
              <button
                type="button"
                data-testid="finder-cancel"
                onClick={onCancel}
                className="h-8 px-3 rounded-ctrl border border-edge-subtle text-micro inline-flex items-center gap-1.5 hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40"
              >
                <XCircle className="h-3.5 w-3.5" aria-hidden /> Cancel
              </button>
            )}
          </div>
          <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-compact">
            <div>
              <dt className="text-micro text-ink-muted">Sources selected</dt>
              <dd className="tabnum font-medium">{progress.sources_selected ?? 0}</dd>
            </div>
            <div>
              <dt className="text-micro text-ink-muted">Sources completed</dt>
              <dd className="tabnum font-medium">{progress.sources_completed ?? 0}</dd>
            </div>
            <div>
              <dt className="text-micro text-ink-muted">Offers found</dt>
              <dd className="tabnum font-medium">{progress.offers_found ?? 0}</dd>
            </div>
            <div>
              <dt className="text-micro text-ink-muted">Verified / Review</dt>
              <dd className="tabnum font-medium">
                {progress.verified_offers ?? 0} / {progress.review_offers ?? 0}
              </dd>
            </div>
          </dl>
          {progress.message && (
            <p className="mt-3 text-compact text-ink-secondary" data-testid="progress-message">
              {progress.message}
            </p>
          )}
          {job?.cancel_message && (
            <p className="mt-2 text-compact text-ink-secondary" data-testid="cancel-message">
              {job.cancel_message}
            </p>
          )}
          {results?.budget_message && (
            <p className="mt-2 text-compact text-amber-700" data-testid="budget-message">
              {results.budget_message}
            </p>
          )}
        </Panel>
      )}

      {results && !results.provisional && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-stitle text-ink-primary">Results</h2>
            <button
              type="button"
              data-testid="toggle-filters"
              onClick={() => setShowFilters((v) => !v)}
              className="h-8 px-2.5 rounded-ctrl border border-edge-subtle text-micro inline-flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40"
            >
              <Filter className="h-3.5 w-3.5" aria-hidden /> Filters
            </button>
          </div>

          {showFilters && (
            <Panel className="p-3" testid="finder-filters">
              <div className="grid sm:grid-cols-3 md:grid-cols-4 gap-2">
                <label className="inline-flex items-center gap-2 text-compact">
                  <input
                    type="checkbox"
                    checked={filters.verifiedOnly}
                    onChange={(e) => setFilters({ ...filters, verifiedOnly: e.target.checked })}
                  />
                  Verified only
                </label>
                <label className="inline-flex items-center gap-2 text-compact">
                  <input
                    type="checkbox"
                    checked={filters.inStock}
                    onChange={(e) => setFilters({ ...filters, inStock: e.target.checked })}
                  />
                  In stock
                </label>
                <label className="inline-flex items-center gap-2 text-compact">
                  <input
                    type="checkbox"
                    checked={filters.positiveRoi}
                    onChange={(e) => setFilters({ ...filters, positiveRoi: e.target.checked })}
                  />
                  Positive ROI
                </label>
                <Field id={`${formId}-ff`} label="Fulfillment">
                  <select
                    id={`${formId}-ff`}
                    className={inputClassName()}
                    value={filters.fulfillment}
                    onChange={(e) => setFilters({ ...filters, fulfillment: e.target.value })}
                  >
                    <option value="">Any</option>
                    <option value="shipping">Shipping</option>
                    <option value="pickup">Pickup</option>
                    <option value="delivery">Delivery</option>
                  </select>
                </Field>
                <Field id={`${formId}-fsrc`} label="Source">
                  <input
                    id={`${formId}-fsrc`}
                    className={inputClassName()}
                    value={filters.source}
                    onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                  />
                </Field>
                <Field id={`${formId}-fminroi`} label="Min ROI">
                  <input
                    id={`${formId}-fminroi`}
                    type="number"
                    className={inputClassName()}
                    value={filters.minimumRoi}
                    onChange={(e) => setFilters({ ...filters, minimumRoi: e.target.value })}
                  />
                </Field>
              </div>
            </Panel>
          )}

          {results.ai_explanation?.available && results.ai_explanation?.summary ? (
            <Panel className="p-4" testid="ai-explanation">
              <div className="flex items-center gap-2 text-compact font-medium text-ink-primary">
                <Sparkles className="h-4 w-4 text-signal-blue" aria-hidden />
                {results.ai_explanation.label || "AI-generated explanation"}
              </div>
              <p className="mt-2 text-compact text-ink-secondary">{results.ai_explanation.summary}</p>
            </Panel>
          ) : null}

          <section aria-labelledby="verified-heading" data-testid="verified-section">
            <h3 id="verified-heading" className="text-body font-medium text-ink-primary mb-2">
              Best Verified Matches
              <span className="ml-2 text-micro text-ink-muted tabnum">({verifiedFiltered.length})</span>
            </h3>
            {verifiedFiltered.length === 0 ? (
              <p className="text-compact text-ink-muted mb-4">No verified matches yet.</p>
            ) : (
              <div className="grid gap-3 mb-6">
                {verifiedFiltered.map((o, i) => (
                  <OfferCard
                    key={offerKey(o, "verified", i)}
                    offer={o}
                    bucket="verified"
                    index={i}
                    jobId={job?.job_id}
                    onSave={onSave}
                    onDismiss={onDismiss}
                    verified
                  />
                ))}
              </div>
            )}
          </section>

          <section aria-labelledby="review-heading" data-testid="review-section">
            <h3 id="review-heading" className="text-body font-medium text-ink-primary mb-2">
              Needs Review
              <span className="ml-2 text-micro text-ink-muted tabnum">({reviewFiltered.length})</span>
            </h3>
            {reviewFiltered.length === 0 ? (
              <p className="text-compact text-ink-muted mb-4">No review matches.</p>
            ) : (
              <div className="grid gap-3 mb-6">
                {reviewFiltered.map((o, i) => (
                  <OfferCard
                    key={offerKey(o, "review", i)}
                    offer={o}
                    bucket="review"
                    index={i}
                    jobId={job?.job_id}
                    onSave={onSave}
                    onDismiss={onDismiss}
                    verified={false}
                  />
                ))}
              </div>
            )}
          </section>

          <p className="text-compact text-ink-muted" data-testid="rejected-summary">
            Rejected summary: {results.rejected_count ?? 0} offer
            {(results.rejected_count ?? 0) === 1 ? "" : "s"} rejected by match policy.
          </p>
        </>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel className="p-4" testid="search-history">
          <h2 className="text-stitle text-ink-primary mb-3">Recent searches</h2>
          {recent.length === 0 ? (
            <p className="text-compact text-ink-muted">No searches yet.</p>
          ) : (
            <ul className="space-y-2">
              {recent.map((row) => (
                <li
                  key={row.job_id}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-edge-subtle last:border-0 pb-2"
                >
                  <div className="min-w-0">
                    <p className="text-compact font-medium text-ink-primary truncate">
                      {row.query || "Search"}
                    </p>
                    <p className="text-micro text-ink-muted">
                      {row.search_mode} · {row.status} · V {row.verified_count} / R {row.review_count}
                      {row.best_roi != null ? ` · best ROI ${pct(row.best_roi)}` : ""}
                      {row.created_at ? ` · ${ago(row.created_at)}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      className="h-8 px-2.5 rounded-ctrl border border-edge-subtle text-micro"
                      onClick={() => reopen(row.job_id)}
                    >
                      Reopen
                    </button>
                    {row.active ? (
                      <button
                        type="button"
                        className="h-8 px-2.5 rounded-ctrl border border-edge-subtle text-micro"
                        onClick={async () => {
                          await api.productSearchCancel(row.job_id);
                          loadMeta();
                        }}
                      >
                        Cancel
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel className="p-4" testid="saved-opportunities">
          <h2 className="text-stitle text-ink-primary mb-3">Saved opportunities</h2>
          {savedOpps.length === 0 ? (
            <p className="text-compact text-ink-muted">No saved opportunities yet.</p>
          ) : (
            <ul className="space-y-2">
              {savedOpps.map((opp) => (
                <li key={opp.id || opp.opportunity_key} className="text-compact border-b border-edge-subtle last:border-0 pb-2">
                  <p className="font-medium text-ink-primary truncate">
                    {opp.walmart?.title || opp.source?.name || opp.source?.source_retailer || "Opportunity"}
                  </p>
                  <p className="text-micro text-ink-muted">
                    {(opp.saved_label || opp.match?.status || "saved")}
                    {opp.match?.status === "review" ? " · Saved for review" : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
